import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  FormHelperText,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  MenuItem,
  TextField,
  CircularProgress,
} from '@mui/material';
import {
  LightMode,
  DarkMode,
  SettingsBrightness,
  Translate,
  InfoOutlined,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useUserPreferences } from '@/contexts/UserPreferencesContext';
import { config } from '@/config/environment';
import { USER_PREF_KEYS } from '@/services/userPreferencesService';
import { ACCENT_COLORS, type AccentColorKey } from '@/theme';
import { WhatsNew } from '@/features/release-notes/WhatsNew';

const AVAILABLE_LANGUAGES = [
  { code: 'en', label: 'English', nativeLabel: 'English' },
  { code: 'sv', label: 'Swedish', nativeLabel: 'Svenska' },
];

// Date/time/number format locales, decoupled from the display language. Empty value = follow
// the display language ("Same as language").
const FORMAT_LOCALES = [
  { value: 'en-US', label: 'English (United States)' },
  { value: 'sv-SE', label: 'Svenska (Sverige)' },
];

const LANDING_PAGE_OPTIONS = [
  { path: '/', label: 'Dashboard' },
  { path: '/shared/dashboard', label: 'Executive Dashboard' },
  { path: '/taskmanagement/inbox', label: 'Task Inbox' },
  { path: '/sales/dashboard', label: 'Sales Overview' },
];

export function UserSettingsPage() {
  const { t } = useTranslation();
  const {
    isLoading,
    theme,
    language,
    formatLocalePref,
    accentColor,
    landingPage,
    emailNotifications,
    updatePreference,
  } = useUserPreferences();

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 800, overflow: 'auto' }}>
      <Typography variant="h4" gutterBottom>
        {t('settings.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('settings.subtitle')}
      </Typography>

      <Stack spacing={3}>
        {/* Appearance */}
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>{t('settings.appearance')}</Typography>

            <Stack spacing={3}>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('settings.theme')}</Typography>
                <ToggleButtonGroup
                  value={theme}
                  exclusive
                  onChange={(_e, val) => val && updatePreference(USER_PREF_KEYS.theme, val)}
                  size="small"
                >
                  <ToggleButton value="light">
                    <LightMode sx={{ mr: 0.5, fontSize: 18 }} /> {t('settings.themeLight')}
                  </ToggleButton>
                  <ToggleButton value="dark">
                    <DarkMode sx={{ mr: 0.5, fontSize: 18 }} /> {t('settings.themeDark')}
                  </ToggleButton>
                  <ToggleButton value="system">
                    <SettingsBrightness sx={{ mr: 0.5, fontSize: 18 }} /> {t('settings.themeSystem')}
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('settings.accentColor')}</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {(Object.entries(ACCENT_COLORS) as [AccentColorKey, { main: string; label: string }][]).map(
                    ([key, { main, label }]) => (
                      <Box
                        key={key}
                        onClick={() => updatePreference(USER_PREF_KEYS.accentColor, key)}
                        title={label}
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 1,
                          bgcolor: main,
                          cursor: 'pointer',
                          border: accentColor === key ? '3px solid' : '2px solid transparent',
                          borderColor: accentColor === key ? 'text.primary' : 'transparent',
                          transition: 'border-color 0.15s, transform 0.15s',
                          '&:hover': { transform: 'scale(1.1)' },
                        }}
                      />
                    ),
                  )}
                </Box>
                <FormHelperText>
                  {t('settings.accentColorHelp')}
                </FormHelperText>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* Language */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Translate sx={{ fontSize: 20, color: 'text.secondary' }} />
              <Typography variant="h6">{t('settings.language')}</Typography>
            </Box>
            <Stack spacing={3}>
              <TextField
                select
                label={t('settings.preferredLanguage')}
                value={language}
                onChange={(e) => updatePreference(USER_PREF_KEYS.language, e.target.value)}
                fullWidth
                helperText={t('settings.languageHelp')}
              >
                {AVAILABLE_LANGUAGES.map((lang) => (
                  <MenuItem key={lang.code} value={lang.code}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>{lang.nativeLabel}</span>
                      {lang.nativeLabel !== lang.label && (
                        <Typography variant="body2" color="text.secondary" component="span">
                          ({lang.label})
                        </Typography>
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                label={t('settings.formatLocale')}
                value={formatLocalePref}
                onChange={(e) => updatePreference(USER_PREF_KEYS.formatLocale, e.target.value)}
                fullWidth
                helperText={t('settings.formatLocaleHelp')}
              >
                <MenuItem value="">
                  <Typography variant="body2" color="text.secondary" component="span">
                    {t('settings.formatLocaleSameAsLanguage')}
                  </Typography>
                </MenuItem>
                {FORMAT_LOCALES.map((loc) => (
                  <MenuItem key={loc.value} value={loc.value}>
                    {loc.label}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          </CardContent>
        </Card>

        {/* Navigation */}
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>{t('settings.navigation')}</Typography>
            <TextField
              select
              label={t('settings.defaultLandingPage')}
              value={landingPage}
              onChange={(e) => updatePreference(USER_PREF_KEYS.landingPage, e.target.value)}
              fullWidth
              helperText={t('settings.landingPageHelp')}
            >
              {LANDING_PAGE_OPTIONS.map((opt) => (
                <MenuItem key={opt.path} value={opt.path}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          </CardContent>
        </Card>

        {/* Notifications */}
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>{t('settings.notifications')}</Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={emailNotifications}
                  onChange={(e) =>
                    updatePreference(USER_PREF_KEYS.emailNotifications, String(e.target.checked))
                  }
                />
              }
              label={t('settings.emailNotifications')}
            />
            <FormHelperText>{t('settings.emailNotificationsHelp')}</FormHelperText>
          </CardContent>
        </Card>
        {/* About */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <InfoOutlined sx={{ fontSize: 20, color: 'text.secondary' }} />
              <Typography variant="h6">{t('settings.about', { defaultValue: 'About' })}</Typography>
            </Box>
            <Stack spacing={1.5}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">{t('settings.version', { defaultValue: 'Version' })}</Typography>
                <Typography variant="body2" fontWeight={500}>{config.appVersion}</Typography>
              </Box>
              {config.buildSha && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">{t('settings.buildSha', { defaultValue: 'Build' })}</Typography>
                  <Typography variant="body2" fontFamily="monospace" sx={{ fontSize: '0.8rem' }}>
                    {config.buildSha.substring(0, 7)}
                  </Typography>
                </Box>
              )}
            </Stack>
            <WhatsNew />
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
}

export default UserSettingsPage;
